import React, { useState, useEffect } from "react";
import Layout from '../pages/Layout';
import Advertise from "../pages/Advertise";

const ProfitPerCustomer = () => {
  // State variables for Profit Per Customer calculation
  const [totalProfit, setTotalProfit] = useState(0); // Total profit earned
  const [numberOfCustomers, setNumberOfCustomers] = useState(0); // Number of customers
  const [profitPerCustomer, setProfitPerCustomer] = useState(0); // Calculated profit per customer

  // Event handlers to update state values
  const handleTotalProfitChange = (e) => setTotalProfit(parseFloat(e.target.value) || 0);
  const handleNumberOfCustomersChange = (e) => setNumberOfCustomers(parseInt(e.target.value) || 0);

  // useEffect hook to calculate profit per customer when any input changes
  useEffect(() => {

    if (numberOfCustomers > 0 && totalProfit >= 0) {
      const profit = totalProfit / numberOfCustomers;
      setProfitPerCustomer(profit);
    } else {
      setProfitPerCustomer(0);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });


  }, [totalProfit, numberOfCustomers]);

  return (
    <Layout title="Profit Per Customer Calculator" path="profit-per-customer">
      <section className="section-padding" id="profit-per-customer-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* Profit Per Customer Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Profit Per Customer Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Total Profit Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="totalProfit" className="form-label">
                        Total Profit (₹)
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Total Profit:</strong> This is the total profit your company has earned from all customers during a given period."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="totalProfit"
                        placeholder="Enter total profit"
                        value={totalProfit || ""}
                        onChange={handleTotalProfitChange}
                      />
                    </div>

                    {/* Number of Customers Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="numberOfCustomers" className="form-label">
                        Number of Customers
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Number of Customers:</strong> This is the total number of customers your business served during the given period. It could be monthly, quarterly, or annually."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="numberOfCustomers"
                        placeholder="Enter number of customers"
                        value={numberOfCustomers || ""}
                        onChange={handleNumberOfCustomersChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Profit Per Customer:{" "}
                        <span className="fw-bold ms-3">
                          {profitPerCustomer === 0 ? "0.00" : profitPerCustomer.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span> ₹
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Profit Per Customer Calculator
                </h3>

                {/* What is a Profit Per Customer Calculator? */}
                <h4 className="mt-3 mb-3">What is a Profit Per Customer Calculator?</h4>
                <p>
                  A **Profit Per Customer Calculator** helps businesses determine the average profit they make from each customer. This is done by dividing the total profit by the number of customers, providing insight into the profitability of the customer base.
                </p>

                {/* Why Use a Profit Per Customer Calculator? */}
                <h4 className="mt-4 mb-3">Why Use a Profit Per Customer Calculator?</h4>
                <p>
                  This calculator allows businesses to assess the profitability of their customers, optimize marketing strategies, and tailor efforts to high-value customer segments.
                </p>

                {/* Benefits of Using a Profit Per Customer Calculator */}
                <h4 className="mt-4 mb-3">Benefits of Using a Profit Per Customer Calculator</h4>
                <ul>
                  <li><strong>Provides Insight into Customer Profitability:</strong> Identifies which customers are contributing the most to the business's bottom line.</li>
                  <li><strong>Helps in Customer Segmentation and Targeting:</strong> Focuses resources on high-profit customers to increase business efficiency.</li>
                  <li><strong>Useful for Developing Personalized Marketing Strategies:</strong> Allows companies to tailor offers and promotions to their most profitable customer groups.</li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">Who Should Use a Profit Per Customer Calculator?</h4>
                <p>
                  This calculator is suitable for:
                </p>
                <ul>
                  <li><strong>Retailers:</strong> Helps retail businesses analyze customer profitability.</li>
                  <li><strong>E-commerce Businesses:</strong> Useful for online stores to evaluate which customer segments are driving the most revenue.</li>
                  <li><strong>Financial Analysts:</strong> Supports decision-making regarding investments in customer acquisition and retention strategies.</li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">How can I improve profit per customer?</h4>
                <p>
                  To improve profit per customer, businesses can focus on increasing the value of each customer through upselling, cross-selling, and offering personalized products or services. Additionally, reducing customer acquisition costs while retaining high-value customers is key.
                </p>

                <h4 className="mt-4 mb-3">Is profit per customer the same as customer lifetime value (CLV)?</h4>
                <p>
                  Profit per customer is a one-time calculation based on a given period, while Customer Lifetime Value (CLV) is the total profit a company expects to earn from a customer throughout the entire relationship. CLV takes into account future purchases and repeat business, while profit per customer is focused on the current period.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ProfitPerCustomer;
